import { Button } from "@sellar-io/components";
import type {
  IntegrationCreateRequestType,
  IntegrationEnumType,
} from "@sellar-io/api-v2/types";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { Icon, SpinnerLegacy } from "../../../components";
import {
  Dialog,
  DialogNavigation,
  DialogContent,
} from "../../../components/modal/Dialog";
import {
  BUSINESS_INTEGRATION_SETTINGS,
  BUSINESS_INTEGRATION_CONFIRM_SETTINGS,
} from "../../../constants/routes";
import { useIntegrationCreate } from "../../../hooks/api/v2/integration/useIntegrationCreate";
import { useIntegrationList } from "../../../hooks/api/v2/integration/useIntegrationList";
import { useSellarCurrentBusiness } from "../../../hooks/useSellarCurrentBusiness.shared";
import { IntegrationAppDelete } from "./IntegrationAppDelete";
import { IntegrationCard } from "./IntegrationCard";

export const IntegrationApp = ({
  appImage,
  appName,
  integrationType,
  description,
  setMessage,
  guideUrl,
}: {
  guideUrl?: string;
  appImage: string;
  appName: string;
  integrationType: Extract<
    IntegrationEnumType,
    "xero" | "quickbooks" | "freeagent"
  >;
  description: string;
  setMessage: (message: string) => void;
}) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { business: userBusiness } = useSellarCurrentBusiness();

  if (!userBusiness) throw new Error("Cannot determine business");

  const createIntegrationMutation = useIntegrationCreate();
  const integrationQuery = useIntegrationList({});
  const integration = integrationQuery.data?.pages[0]?.find(
    (integration) => integration.type === integrationType,
  );
  const code = searchParams.get("code");
  const integrationState = searchParams.get("state");
  const realmId = searchParams.get("realmId");
  const processedCodeRef = useRef<string | null>(null);

  const shouldCreateIntegration = Boolean(code && integrationState === appName);
  const [active, setActive] = useState(shouldCreateIntegration);
  const onDismiss = () => setActive(false);

  const getConfirmationRoute = () => {
    navigate(`${BUSINESS_INTEGRATION_CONFIRM_SETTINGS}?type=${appName}`);
  };

  useEffect(() => {
    if (
      code &&
      integrationState === appName &&
      processedCodeRef.current !== code
    ) {
      processedCodeRef.current = code;

      let command: IntegrationCreateRequestType;

      if (integrationType === "quickbooks") {
        if (!realmId) {
          queueMicrotask(() => {
            onDismiss();
            setMessage(
              "There was a problem connecting to Quickbooks. Please contact support",
            );
            navigate(BUSINESS_INTEGRATION_SETTINGS, { replace: true });
          });
          return;
        }

        command = {
          type: integrationType,
          authorizationCode: code,
          realmId,
        };
      } else {
        command = {
          type: integrationType,
          authorizationCode: code,
        };
      }

      createIntegrationMutation.mutate(command, {
        onError: () => {
          onDismiss();
          setMessage(
            `There was a problem connecting to ${appName}. Please contact support`,
          );
        },
        onSettled: () => {
          navigate(BUSINESS_INTEGRATION_SETTINGS, { replace: true });
        },
      });
    }
  }, [
    appName,
    code,
    createIntegrationMutation,
    integrationState,
    integrationType,
    navigate,
    realmId,
    setMessage,
  ]);

  const loading = createIntegrationMutation.isPending;

  return (
    <>
      <IntegrationCard
        appImage={appImage}
        title={appName}
        description={description}
        headerAction={
          integration?.id ? (
            <IntegrationAppDelete
              appName={appName}
              integrationId={integration.id}
              setMessage={setMessage}
            />
          ) : undefined
        }
        actions={
          <>
            <Button onClick={getConfirmationRoute}>
              <Icon icon={"COG"} />
              Configure
            </Button>
            {guideUrl ? (
              <Button
                color={"secondary"}
                onClick={() => window.open(guideUrl, "_blank")}
              >
                <Icon icon={"OPEN_IN_NEW"} />
                VIEW SETUP GUIDE
              </Button>
            ) : null}
          </>
        }
      />

      <Dialog active={active} onDismiss={onDismiss}>
        <DialogNavigation
          onClickLeftIcon={onDismiss}
          leftIcon={"CHEVRON_DOWN"}
          title={"You're all set."}
          description={`You've successfully connected your ${appName} account to Sellar. All new orders you receive via Sellar will be automatically synced with you ${appName} account.`}
        />
        <DialogContent className={"p-4"}>
          {loading && <SpinnerLegacy />}
        </DialogContent>
      </Dialog>
    </>
  );
};
